// GAP-15d — CLV education walkthrough (OddsJam Wave 1).
//
// Pure presentational. No DB, no RPC, no migration. Same
// regression-safe pattern: standalone auth-gated route, NOT in
// steps.ts (education, not a legal gate). 3 screens explaining why
// CLV/process beats outcome — generic, honest, NO fabricated stats,
// NO promises (mirrors the Reports mock-purge doctrine: never invent
// a track record or ROI figure to make a point).

import { useState } from "react";

interface Screen {
  readonly eyebrow: string;
  readonly title: string;
  readonly body: string;
}

const SCREENS: readonly Screen[] = [
  {
    eyebrow: "1 / 3 · The shift",
    title: "Stop asking “did I win?”",
    body:
      "A single bet's result is mostly noise. The question that " +
      "predicts your long-run outcome is different: “was the price I " +
      "took better than where the market closed?” That gap is Closing " +
      "Line Value (CLV).",
  },
  {
    eyebrow: "2 / 3 · Why CLV",
    title: "The closing line is the market's best guess",
    body:
      "By kickoff, the market has absorbed team news, money, and " +
      "information. If you consistently took a better price than that " +
      "closing line, you were on the right side of the market — " +
      "whether or not that particular bet won.",
  },
  {
    eyebrow: "3 / 3 · How STRIKZA uses it",
    title: "Process over outcome",
    body:
      "STRIKZA grades your decisions by CLV and expected value, not by " +
      "win/loss alone. A losing bet taken at a beaten closing line was " +
      "a correct decision. STRIKZA never promises wins — it helps you " +
      "make repeatedly correct decisions.",
  },
];

const WRAP: React.CSSProperties = {
  maxWidth: 600,
  margin: "0 auto",
  padding: "40px 24px 80px",
  color: "var(--se-text, #fff)",
  fontFamily: "var(--se-font-body, system-ui)",
};

export function ClvEducationPage(): JSX.Element {
  const [i, setI] = useState(0);
  const screen = SCREENS[i] ?? SCREENS[0]!;
  const last = i === SCREENS.length - 1;

  return (
    <div data-testid="clv-education" style={WRAP}>
      <div
        data-testid="clv-eyebrow"
        style={{
          fontFamily: "var(--se-font-mono, monospace)",
          fontSize: 10,
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          color: "var(--se-text-muted, #888)",
        }}
      >
        {screen.eyebrow}
      </div>
      <h1 style={{ fontSize: 25, fontWeight: 500, margin: "12px 0 12px" }}>
        {screen.title}
      </h1>
      <p
        style={{
          fontSize: 14,
          lineHeight: 1.65,
          color: "var(--se-text-sec, #aaa)",
          margin: "0 0 32px",
        }}
      >
        {screen.body}
      </p>

      <div style={{ display: "flex", gap: 6, marginBottom: 24 }}>
        {SCREENS.map((_, n) => (
          <span
            key={n}
            style={{
              flex: 1,
              height: 3,
              borderRadius: 999,
              background: n <= i
                ? "var(--se-amber, #d4a017)"
                : "var(--se-border, #2a2a2a)",
            }}
          />
        ))}
      </div>

      <div style={{ display: "flex", gap: 10 }}>
        {i > 0 ? (
          <button
            type="button"
            data-testid="clv-back"
            onClick={() => setI((v) => Math.max(0, v - 1))}
            style={{
              padding: "10px 18px",
              borderRadius: 2,
              border: "1px solid var(--se-border, #2a2a2a)",
              background: "transparent",
              color: "var(--se-text-sec, #aaa)",
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Back
          </button>
        ) : null}
        <button
          type="button"
          data-testid={last ? "clv-done" : "clv-next"}
          onClick={() => setI((v) => Math.min(SCREENS.length - 1, v + 1))}
          disabled={last}
          style={{
            padding: "10px 20px",
            borderRadius: 2,
            border: "none",
            background: "var(--se-amber, #d4a017)",
            color: "var(--se-surface-page, #000)",
            fontSize: 13.5,
            fontWeight: 600,
            cursor: last ? "default" : "pointer",
            opacity: last ? 0.7 : 1,
          }}
        >
          {last ? "You're set ✓" : "Next"}
        </button>
      </div>
    </div>
  );
}
